/**
 * How a count sheet is laid out (decision 220; client, 2026-10-16: "it should
 * group all the styles by vendor … should be able to be shown and printed in
 * grid form"; 2026-10-17: "grouped by vendor then model then the skus (variants
 * live under that sorted)").
 *
 * The rows arrive in sheet order — vendor, model, color, size — so grouping
 * only draws the lines between them. A model's grid is its colors down and its
 * sizes across, in size order; a color and size the model does not come in is
 * an empty cell, never a box to count.
 */

export interface SheetRowLike {
  readonly variantId: string;
  readonly vendorId: string;
  readonly vendorName: string;
  readonly productId: string;
  readonly styleNumber: string;
  readonly styleName: string;
  readonly colorName: string | null;
  readonly size: string | null;
  readonly sizeSort: number;
}

export interface ModelGroup<Row extends SheetRowLike> {
  readonly key: string;
  readonly styleNumber: string;
  readonly styleName: string;
  readonly rows: Row[];
}

export interface VendorGroup<Row extends SheetRowLike> {
  readonly key: string;
  readonly vendorName: string;
  readonly models: ModelGroup<Row>[];
}

/** Vendor by vendor, model by model, in the order the rows came. */
export function groupSheet<Row extends SheetRowLike>(rows: readonly Row[]): VendorGroup<Row>[] {
  const vendors: VendorGroup<Row>[] = [];
  for (const row of rows) {
    let vendor = vendors[vendors.length - 1];
    if (vendor === undefined || vendor.key !== row.vendorId) {
      vendor = { key: row.vendorId, vendorName: row.vendorName, models: [] };
      vendors.push(vendor);
    }
    let model = vendor.models[vendor.models.length - 1];
    if (model === undefined || model.key !== row.productId) {
      model = {
        key: row.productId,
        styleNumber: row.styleNumber,
        styleName: row.styleName,
        rows: [],
      };
      vendor.models.push(model);
    }
    model.rows.push(row);
  }
  return vendors;
}

/**
 * The maker's number printed beside a model's name: the first one a size
 * carries, unless it is only the model number again — Rain often wrote the
 * same code in both, and the heading read it twice (decision 222).
 */
export function makerNumberOf(model: {
  readonly styleNumber: string;
  readonly rows: readonly { readonly mfr: string | null }[];
}): string | null {
  const mfr = model.rows.find((row) => row.mfr !== null && row.mfr.trim() !== '')?.mfr ?? null;
  if (mfr === null) return null;
  return mfr.trim().toUpperCase() === model.styleNumber.trim().toUpperCase() ? null : mfr;
}

export interface Grid<Row extends SheetRowLike> {
  /** Colors down the side, in the order the model's rows have them. */
  readonly colors: (string | null)[];
  /** Sizes across the top, in size order. */
  readonly sizes: (string | null)[];
  /** The size for a color and a size, or undefined when the model has none. */
  readonly cell: (color: string | null, size: string | null) => Row | undefined;
}

export function gridOf<Row extends SheetRowLike>(model: ModelGroup<Row>): Grid<Row> {
  const colors: (string | null)[] = [];
  const sizeOrder = new Map<string | null, number>();
  const cells = new Map<string, Row>();
  const key = (color: string | null, size: string | null) => `${color ?? ''}\u0000${size ?? ''}`;
  for (const row of model.rows) {
    if (!colors.includes(row.colorName)) colors.push(row.colorName);
    const seen = sizeOrder.get(row.size);
    if (seen === undefined || row.sizeSort < seen) sizeOrder.set(row.size, row.sizeSort);
    cells.set(key(row.colorName, row.size), row);
  }
  const sizes = [...sizeOrder.entries()]
    .sort(
      ([sizeA, sortA], [sizeB, sortB]) =>
        sortA - sortB || (sizeA ?? '').localeCompare(sizeB ?? '', undefined, { numeric: true }),
    )
    .map(([size]) => size);
  return { colors, sizes, cell: (color, size) => cells.get(key(color, size)) };
}
